import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Container,
  IconButton,
  CircularProgress,
  Button,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import TranslateIcon from "@mui/icons-material/Translate";
import TuneIcon from "@mui/icons-material/Tune";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import TimelineIcon from "@mui/icons-material/Timeline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RefreshIcon from "@mui/icons-material/Refresh";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EventCsvUpload } from "../components/EventCsvUpload";
import AdminApprovalTools from "../components/AdminApprovalTools";
import NeedsAttentionWordingChip from "../components/NeedsAttentionWordingChip";
import AdminAuditLogPanel from "../components/AdminAuditLogPanel";
import AdminNumberRangeStatusPage from "./AdminNumberRangeStatusPage";
import { useExportCodePreference } from "../context/ExportCodePreferenceContext";
import CustomAlert from "../components/CustomAlert";
import AdminNumberRangeLabelsPanel from "../components/AdminNumberRangeLabelsPanel";
import AdminTranslationPanel from "../components/AdminTranslationPanel";
import AdminWordingGuardPanel from "../components/AdminWordingGuardPanel";
import AdminRoleInfoTooltip from "../components/AdminRoleInfoTooltip";
import AdminEventReassignmentPanel from "../components/AdminEventReassignmentPanel";
import AdminContractRenamePanel from "../components/AdminContractRenamePanel";
import AdminVehicleSoftwareScanPanel from "../components/AdminVehicleSoftwareScanPanel";
import EventsFormDialog from "../components/EventsFormDialog";
import { useAdminReportsApi } from "../hooks/useAdminReportsApi";
import { useWordingGuardApi } from "../hooks/useWordingGuardApi";
import {
  ROLE_ADMIN_EXPORT_SETTINGS,
  ROLE_API_ADMIN_APPROVE,
  ROLE_PORTAL_ADMIN,
} from "../constants/appRoles";
import type { WordingGuardAnalysisResponse } from "../shared/wordingGuard";
import type {
  AdminBacklogRow,
  DevCommentBacklogRow,
  DuplicateContractNameLatestHeadsResponse,
  DuplicateEventCodeVersionResponse,
  DuplicateLegacyEventCodeResponse,
  EmptyContractUserEventRow,
} from "../shared/adminReports";
import { useCreatorWorkspaceApi } from "../hooks/useCreatorWorkspaceApi";
import { formatLocalDateTime } from "../utils/formatLocalDateTime";
import type { UserActivityLog } from "../shared/workspace";

const activityActionLabel = (actionType: string): string => {
  switch (actionType) {
    case "session_created":
      return "Session created";
    case "session_updated":
      return "Session updated";
    case "session_deleted":
      return "Session deleted";
    case "event_created":
      return "Event created";
    case "event_updated":
      return "Event updated";
    case "event_approved":
      return "Event approved";
    case "event_deleted":
      return "Event deleted";
    case "event_translation_queued":
      return "Translation queued";
    default:
      return actionType.replaceAll("_", " ");
  }
};

function buildExportSettingsSavedMessage(useLegacy: boolean): React.ReactNode {
  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        Export settings saved
      </Typography>
      {useLegacy ? (
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
          <strong>Mode: legacy (old) codes.</strong> For non-manufacturer events, all exports (EDF,
          Visu+, Exor alarms XML, Kollmorgen ZIP, PDF, WebHMI, IPC) use the stored legacy PLC code from event
          attributes (<code>event_code_old</code>) when present; otherwise they use the current event code.
          Manufacturer events always use the current code. This applies <strong>server-wide for every user</strong>.
          Export jobs already in progress may still finish using the previous mode.
        </Typography>
      ) : (
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.5 }}>
          <strong>Mode: new system codes.</strong> All of the above exports consistently use the current{" "}
          <code>event_code</code> for non-manufacturer events as well. Manufacturer events are unchanged.
          This applies <strong>server-wide for every user</strong>. Export jobs already in progress may still
          finish using the previous mode.
        </Typography>
      )}
    </Stack>
  );
}

const FLAT_ACCORDION_SX = {
  border: "none",
  boxShadow: "none",
  "&::before": { display: "none" },
  "&.Mui-expanded": { margin: 0 },
} as const;

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const [auditExpanded, setAuditExpanded] = React.useState(false);
  const [approvalToolsExpanded, setApprovalToolsExpanded] = React.useState(false);
  const [translationAdminExpanded, setTranslationAdminExpanded] = React.useState(false);
  const [numberRangeLabelsExpanded, setNumberRangeLabelsExpanded] = React.useState(false);
  const [wordingGuardExpanded, setWordingGuardExpanded] = React.useState(false);
  const [exportCodesExpanded, setExportCodesExpanded] = React.useState(false);
  const [eventReassignmentExpanded, setEventReassignmentExpanded] = React.useState(false);
  const [contractRenameExpanded, setContractRenameExpanded] = React.useState(false);
  const [numberRangeDataVersion, setNumberRangeDataVersion] = React.useState(0);
  const [numberRangeUsageExpanded, setNumberRangeUsageExpanded] = React.useState(false);
  const [headerMetricsLoading, setHeaderMetricsLoading] = React.useState(true);
  const [unapprovedTodoCount, setUnapprovedTodoCount] = React.useState(0);
  const [devCommentTodoCount, setDevCommentTodoCount] = React.useState(0);
  const [duplicateCodeGroupCount, setDuplicateCodeGroupCount] = React.useState(0);
  const [duplicateContractNameGroupCount, setDuplicateContractNameGroupCount] = React.useState(0);
  const [wordingViolationEntryCount, setWordingViolationEntryCount] = React.useState(0);
  const [wordingLatestAnalysis, setWordingLatestAnalysis] =
    React.useState<WordingGuardAnalysisResponse | null>(null);
  /** Wording violation scan is expensive; it is not run on page load. */
  const [wordingScanState, setWordingScanState] = React.useState<
    "not_run" | "loading" | "done" | "error"
  >("not_run");
  const [prefetchedApprovalRows, setPrefetchedApprovalRows] =
    React.useState<AdminBacklogRow[] | null>(null);
  const [prefetchedTodoRows, setPrefetchedTodoRows] =
    React.useState<DevCommentBacklogRow[] | null>(null);
  const [prefetchedDuplicateReport, setPrefetchedDuplicateReport] =
    React.useState<DuplicateEventCodeVersionResponse | null>(null);
  const [prefetchedDuplicateLegacyReport, setPrefetchedDuplicateLegacyReport] =
    React.useState<DuplicateLegacyEventCodeResponse | null>(null);
  const [prefetchedDuplicateContractNameReport, setPrefetchedDuplicateContractNameReport] =
    React.useState<DuplicateContractNameLatestHeadsResponse | null>(null);
  const [emptyContractUserEventCount, setEmptyContractUserEventCount] = React.useState(0);
  const [prefetchedEmptyContractRows, setPrefetchedEmptyContractRows] = React.useState<
    EmptyContractUserEventRow[] | null
  >(null);
  const {
    fetchUnapprovedBacklog,
    fetchDevCommentBacklog,
    fetchDuplicateUserEventCodeVersion,
    fetchDuplicateLegacyEventCode,
    fetchDuplicateContractNamesLatestHeads,
    fetchUserEventsEmptyContractLatest,
  } = useAdminReportsApi();
  const { analyzeWordingGuard } = useWordingGuardApi();
  const bumpNumberRangeData = React.useCallback(() => {
    setNumberRangeDataVersion((v) => v + 1);
  }, []);
  const {
    useLegacyEventCodesInExport,
    setUseLegacyEventCodesInExport,
    settingsLoaded,
    settingsLoadError,
  } = useExportCodePreference();
  const { getAdminActivity } = useCreatorWorkspaceApi();
  const [exportSettingsError, setExportSettingsError] = React.useState<string | null>(null);
  const [exportSettingsSavedOpen, setExportSettingsSavedOpen] = React.useState(false);
  const [exportSettingsSavedBody, setExportSettingsSavedBody] =
    React.useState<React.ReactNode>(null);
  const [adminActivityRows, setAdminActivityRows] = React.useState<UserActivityLog[]>([]);
  const [adminActivityLoading, setAdminActivityLoading] = React.useState(false);
  const [adminUserFilter, setAdminUserFilter] = React.useState("");
  const [adminActivityOffset, setAdminActivityOffset] = React.useState(0);
  const [adminActivityLimit] = React.useState(20);
  const [adminActivityTotal, setAdminActivityTotal] = React.useState(0);
  const [editEventId, setEditEventId] = React.useState<number | null>(null);
  const [vehicleScanExpanded, setVehicleScanExpanded] = React.useState(false);

  const [naRefreshBusy, setNaRefreshBusy] = React.useState({
    approvals: false,
    todos: false,
    dupCode: false,
    dupContract: false,
    empty: false,
    wording: false,
    all: false,
  });
  const [needsAttentionMenuAnchor, setNeedsAttentionMenuAnchor] =
    React.useState<null | HTMLElement>(null);

  const approvalsNeedsAttention =
    unapprovedTodoCount > 0 ||
    devCommentTodoCount > 0 ||
    duplicateCodeGroupCount > 0 ||
    duplicateContractNameGroupCount > 0 ||
    emptyContractUserEventCount > 0;

  const runWordingHeaderScan = React.useCallback(async () => {
    setWordingScanState("loading");
    try {
      const wording = await analyzeWordingGuard("latest");
      setWordingViolationEntryCount(wording?.total_entries ?? 0);
      setWordingLatestAnalysis(wording ?? null);
      setWordingScanState("done");
    } catch {
      setWordingViolationEntryCount(0);
      setWordingLatestAnalysis(null);
      setWordingScanState("error");
    }
  }, [analyzeWordingGuard]);

  const fetchAndApplyNeedsAttentionBundle = React.useCallback(async () => {
    const [unapproved, comments, duplicates, legacyDuplicates, dupContracts, emptyContract] = await Promise.all([
      fetchUnapprovedBacklog({ limit: 100 }),
      fetchDevCommentBacklog({ limit: 100 }),
      fetchDuplicateUserEventCodeVersion({ limit: 100 }),
      fetchDuplicateLegacyEventCode({ limit: 100 }),
      fetchDuplicateContractNamesLatestHeads({ limit: 100 }),
      fetchUserEventsEmptyContractLatest({ limit: 1, offset: 0 }),
    ]);
    setUnapprovedTodoCount(unapproved?.total ?? unapproved?.count ?? unapproved?.results?.length ?? 0);
    setDevCommentTodoCount(comments?.total ?? comments?.count ?? comments?.results?.length ?? 0);
    setDuplicateCodeGroupCount(
      (duplicates?.total_duplicate_groups ?? 0) + (legacyDuplicates?.total_duplicate_groups ?? 0)
    );
    setDuplicateContractNameGroupCount(dupContracts?.total_duplicate_contract_groups ?? 0);
    setEmptyContractUserEventCount(emptyContract?.total ?? emptyContract?.count ?? 0);
    setPrefetchedApprovalRows(unapproved?.results ?? []);
    setPrefetchedTodoRows(comments?.results ?? []);
    setPrefetchedDuplicateReport(duplicates ?? null);
    setPrefetchedDuplicateLegacyReport(legacyDuplicates ?? null);
    setPrefetchedDuplicateContractNameReport(dupContracts ?? null);
    setPrefetchedEmptyContractRows(null);
  }, [
    fetchUnapprovedBacklog,
    fetchDevCommentBacklog,
    fetchDuplicateUserEventCodeVersion,
    fetchDuplicateLegacyEventCode,
    fetchDuplicateContractNamesLatestHeads,
    fetchUserEventsEmptyContractLatest,
  ]);

  const runNaWith = React.useCallback(
    async (key: keyof typeof naRefreshBusy, fn: () => Promise<void>) => {
      setNaRefreshBusy((b) => ({ ...b, [key]: true }));
      try {
        await fn();
      } finally {
        setNaRefreshBusy((b) => ({ ...b, [key]: false }));
      }
    },
    []
  );

  const reloadApprovals = React.useCallback(() => {
    return runNaWith("approvals", async () => {
      const unapproved = await fetchUnapprovedBacklog({ limit: 100 });
      setUnapprovedTodoCount(unapproved?.total ?? unapproved?.count ?? unapproved?.results?.length ?? 0);
      setPrefetchedApprovalRows(unapproved?.results ?? []);
    });
  }, [runNaWith, fetchUnapprovedBacklog]);

  const reloadDevTodos = React.useCallback(() => {
    return runNaWith("todos", async () => {
      const comments = await fetchDevCommentBacklog({ limit: 100 });
      setDevCommentTodoCount(comments?.total ?? comments?.count ?? comments?.results?.length ?? 0);
      setPrefetchedTodoRows(comments?.results ?? []);
    });
  }, [runNaWith, fetchDevCommentBacklog]);

  const reloadDupCodes = React.useCallback(() => {
    return runNaWith("dupCode", async () => {
      const [duplicates, legacyDuplicates] = await Promise.all([
        fetchDuplicateUserEventCodeVersion({ limit: 100 }),
        fetchDuplicateLegacyEventCode({ limit: 100 }),
      ]);
      setDuplicateCodeGroupCount(
        (duplicates?.total_duplicate_groups ?? 0) + (legacyDuplicates?.total_duplicate_groups ?? 0)
      );
      setPrefetchedDuplicateReport(duplicates ?? null);
      setPrefetchedDuplicateLegacyReport(legacyDuplicates ?? null);
    });
  }, [runNaWith, fetchDuplicateUserEventCodeVersion, fetchDuplicateLegacyEventCode]);

  const reloadDupContracts = React.useCallback(() => {
    return runNaWith("dupContract", async () => {
      const dupContracts = await fetchDuplicateContractNamesLatestHeads({ limit: 100 });
      setDuplicateContractNameGroupCount(dupContracts?.total_duplicate_contract_groups ?? 0);
      setPrefetchedDuplicateContractNameReport(dupContracts ?? null);
    });
  }, [runNaWith, fetchDuplicateContractNamesLatestHeads]);

  const reloadEmptyContracts = React.useCallback(() => {
    return runNaWith("empty", async () => {
      const emptyContract = await fetchUserEventsEmptyContractLatest({ limit: 1, offset: 0 });
      setEmptyContractUserEventCount(emptyContract?.total ?? emptyContract?.count ?? 0);
    });
  }, [runNaWith, fetchUserEventsEmptyContractLatest]);

  const reloadWordingOnly = React.useCallback(() => {
    return runNaWith("wording", () => runWordingHeaderScan());
  }, [runNaWith, runWordingHeaderScan]);

  const reloadAllNeedsAttention = React.useCallback(() => {
    return runNaWith("all", async () => {
      await fetchAndApplyNeedsAttentionBundle();
      await runWordingHeaderScan();
    });
  }, [runNaWith, fetchAndApplyNeedsAttentionBundle, runWordingHeaderScan]);

  /** Duplicate code/version, duplicate contract names, empty-contract totals — refetch after edits or when opening Review queue. */
  const refreshDataQualityMetrics = React.useCallback(async () => {
    try {
      const [duplicates, legacyDuplicates, dupContracts, emptyContract] = await Promise.all([
        fetchDuplicateUserEventCodeVersion({ limit: 100 }),
        fetchDuplicateLegacyEventCode({ limit: 100 }),
        fetchDuplicateContractNamesLatestHeads({ limit: 100 }),
        fetchUserEventsEmptyContractLatest({ limit: 1, offset: 0 }),
      ]);
      setDuplicateCodeGroupCount(
        (duplicates?.total_duplicate_groups ?? 0) + (legacyDuplicates?.total_duplicate_groups ?? 0)
      );
      setDuplicateContractNameGroupCount(dupContracts?.total_duplicate_contract_groups ?? 0);
      setEmptyContractUserEventCount(emptyContract?.total ?? emptyContract?.count ?? 0);
      setPrefetchedDuplicateReport(duplicates ?? null);
      setPrefetchedDuplicateLegacyReport(legacyDuplicates ?? null);
      setPrefetchedDuplicateContractNameReport(dupContracts ?? null);
    } catch {
      // Leave existing counts on failure.
    }
  }, [
    fetchDuplicateUserEventCodeVersion,
    fetchDuplicateLegacyEventCode,
    fetchDuplicateContractNamesLatestHeads,
    fetchUserEventsEmptyContractLatest,
  ]);

  React.useEffect(() => {
    if (!approvalToolsExpanded) return;
    void refreshDataQualityMetrics();
  }, [approvalToolsExpanded, refreshDataQualityMetrics]);

  React.useEffect(() => {
    const raw = searchParams.get("open_event_id");
    if (!raw) return;
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      setEditEventId(parsed);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open_event_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    let cancelled = false;
    const loadHeaderMetrics = async () => {
      setHeaderMetricsLoading(true);
      try {
        await fetchAndApplyNeedsAttentionBundle();
        if (cancelled) return;
      } catch {
        if (!cancelled) {
          setUnapprovedTodoCount(0);
          setDevCommentTodoCount(0);
          setDuplicateCodeGroupCount(0);
          setDuplicateContractNameGroupCount(0);
          setEmptyContractUserEventCount(0);
          setPrefetchedApprovalRows(null);
          setPrefetchedTodoRows(null);
          setPrefetchedDuplicateReport(null);
          setPrefetchedDuplicateLegacyReport(null);
          setPrefetchedDuplicateContractNameReport(null);
          setPrefetchedEmptyContractRows(null);
        }
      } finally {
        if (!cancelled) setHeaderMetricsLoading(false);
      }
    };
    void loadHeaderMetrics();
    return () => {
      cancelled = true;
    };
  }, [fetchAndApplyNeedsAttentionBundle]);

  const loadAdminActivity = React.useCallback(async () => {
    setAdminActivityLoading(true);
    try {
      const result = await getAdminActivity({
        userUuid: adminUserFilter,
        limit: adminActivityLimit,
        offset: adminActivityOffset,
      });
      setAdminActivityRows(result.results ?? []);
      setAdminActivityTotal(result.total ?? 0);
    } catch {
      setExportSettingsError("Failed to load user activity log.");
    } finally {
      setAdminActivityLoading(false);
    }
  }, [getAdminActivity, adminUserFilter, adminActivityLimit, adminActivityOffset]);

  React.useEffect(() => {
    void loadAdminActivity();
  }, [loadAdminActivity]);

  const approvalsChipBusy =
    headerMetricsLoading || naRefreshBusy.all || naRefreshBusy.approvals;
  const todosChipBusy = headerMetricsLoading || naRefreshBusy.all || naRefreshBusy.todos;
  const dupCodeChipBusy =
    headerMetricsLoading || naRefreshBusy.all || naRefreshBusy.dupCode;
  const dupContractChipBusy =
    headerMetricsLoading || naRefreshBusy.all || naRefreshBusy.dupContract;
  const emptyChipBusy =
    headerMetricsLoading || naRefreshBusy.all || naRefreshBusy.empty;
  const wordingChipBusy =
    wordingScanState === "loading" || naRefreshBusy.all || naRefreshBusy.wording;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: theme.palette.text.primary }}>
        Admin
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
        Approvals, audit trail, CSV compare, and number range usage. Expand a section to use it; list data
        loads when you open it.
      </Typography>
      {settingsLoadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {settingsLoadError}
        </Alert>
      )}

      <Stack spacing={3}>
        <Paper
          variant="outlined"
          sx={{
            borderColor: "warning.light",
            backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255, 167, 38, 0.07)" : "rgba(255, 152, 0, 0.04)",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
                  Needs Attention
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Start here for approvals, backlog items, and data-quality checks. Use refresh all or the menu to
                  reload one metric. Wording scan does not run on page open — run it from the chip or wording guard when
                  needed.
                </Typography>
              </Box>
              <Stack direction="row" spacing={0} alignItems="center" sx={{ mt: -0.25 }}>
                <Tooltip title="Refresh all backlog counts, data-quality metrics, and wording scan">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Refresh all Needs Attention metrics"
                      disabled={headerMetricsLoading || naRefreshBusy.all}
                      onClick={() => void reloadAllNeedsAttention()}
                    >
                      {naRefreshBusy.all ? (
                        <CircularProgress size={20} thickness={5} />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Refresh one metric…">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Choose metric to refresh"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(needsAttentionMenuAnchor)}
                      disabled={headerMetricsLoading || naRefreshBusy.all}
                      onClick={(e) => setNeedsAttentionMenuAnchor(e.currentTarget)}
                    >
                      <ArrowDropDownIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <Menu
              anchorEl={needsAttentionMenuAnchor}
              open={Boolean(needsAttentionMenuAnchor)}
              onClose={() => setNeedsAttentionMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                disabled={approvalsChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadApprovals();
                }}
              >
                Open approvals only
              </MenuItem>
              <MenuItem
                disabled={todosChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadDevTodos();
                }}
              >
                Open TODOs only
              </MenuItem>
              <MenuItem
                disabled={dupCodeChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadDupCodes();
                }}
              >
                Duplicate event codes only
              </MenuItem>
              <MenuItem
                disabled={dupContractChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadDupContracts();
                }}
              >
                Duplicate contract names only
              </MenuItem>
              <MenuItem
                disabled={emptyChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadEmptyContracts();
                }}
              >
                Empty contract names only
              </MenuItem>
              <MenuItem
                disabled={wordingChipBusy}
                onClick={() => {
                  setNeedsAttentionMenuAnchor(null);
                  void reloadWordingOnly();
                }}
              >
                Wording scan only
              </MenuItem>
            </Menu>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mt: 1.25 }}
              flexWrap="wrap"
              aria-busy={
                headerMetricsLoading ||
                wordingScanState === "loading" ||
                naRefreshBusy.all
              }
            >
              <Tooltip
                title={
                  approvalsChipBusy
                    ? "Loading approval backlog…"
                    : "Latest version per event key (code, type, contract) with no approval timestamp. Non-manufacturer events only. Opens the review queue accordion."
                }
              >
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                  <Chip
                    size="small"
                    color={
                      approvalsChipBusy
                        ? "default"
                        : unapprovedTodoCount > 0
                          ? "warning"
                          : "success"
                    }
                    icon={
                      approvalsChipBusy ? (
                        <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                      ) : undefined
                    }
                    label={
                      approvalsChipBusy ? "Open approvals" : `Open approvals: ${unapprovedTodoCount}`
                    }
                    onClick={() => setApprovalToolsExpanded(true)}
                    clickable
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  todosChipBusy
                    ? "Loading dev-comment backlog…"
                    : "Latest versions with a non-empty development comment (admin TODO). Opens the review queue accordion."
                }
              >
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                  <Chip
                    size="small"
                    color={
                      todosChipBusy
                        ? "default"
                        : devCommentTodoCount > 0
                          ? "warning"
                          : "success"
                    }
                    icon={
                      todosChipBusy ? (
                        <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                      ) : undefined
                    }
                    label={todosChipBusy ? "Open TODOs" : `Open TODOs: ${devCommentTodoCount}`}
                    onClick={() => setApprovalToolsExpanded(true)}
                    clickable
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  dupCodeChipBusy
                    ? "Checking for duplicate event codes…"
                    : "Combined count of duplicate system code/version groups and duplicate stored legacy-code groups for non-manufacturer UserEvents."
                }
              >
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                  <Chip
                    size="small"
                    color={
                      dupCodeChipBusy
                        ? "default"
                        : duplicateCodeGroupCount > 0
                          ? "warning"
                          : "success"
                    }
                    icon={
                      dupCodeChipBusy ? (
                        <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                      ) : undefined
                    }
                    label={
                      dupCodeChipBusy
                        ? "Duplicate event codes"
                        : `Duplicate event codes: ${duplicateCodeGroupCount}`
                    }
                    onClick={() => setApprovalToolsExpanded(true)}
                    clickable
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  dupContractChipBusy
                    ? "Checking duplicate contract names…"
                    : "Non-manufacturer UserEvents (not superseded) where the same normalized contract name appears on more than one event code — latest row per module and event code (highest version). Case-insensitive; whitespace collapsed."
                }
              >
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                  <Chip
                    size="small"
                    color={
                      dupContractChipBusy
                        ? "default"
                        : duplicateContractNameGroupCount > 0
                          ? "warning"
                          : "success"
                    }
                    icon={
                      dupContractChipBusy ? (
                        <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                      ) : undefined
                    }
                    label={
                      dupContractChipBusy
                        ? "Duplicate contract names"
                        : `Duplicate contract names: ${duplicateContractNameGroupCount}`
                    }
                    onClick={() => setApprovalToolsExpanded(true)}
                    clickable
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title={
                  emptyChipBusy
                    ? "Loading empty contract name count…"
                    : "UserEvents with an empty or whitespace-only contract name (trimmed), excluding manufacturer and reassignment-superseded. One row per event code (highest version). Opens the review queue accordion."
                }
              >
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle" }}>
                  <Chip
                    size="small"
                    color={
                      emptyChipBusy
                        ? "default"
                        : emptyContractUserEventCount > 0
                          ? "warning"
                          : "success"
                    }
                    icon={
                      emptyChipBusy ? (
                        <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                      ) : undefined
                    }
                    label={
                      emptyChipBusy
                        ? "Empty contract names"
                        : `Empty contract names: ${emptyContractUserEventCount}`
                    }
                    onClick={() => setApprovalToolsExpanded(true)}
                    clickable
                  />
                </Box>
              </Tooltip>
              <NeedsAttentionWordingChip
                scanState={wordingScanState}
                violationEntryCount={wordingViolationEntryCount}
                busy={wordingChipBusy}
                onClick={() => {
                  setWordingGuardExpanded(true);
                  if (wordingScanState === "not_run" || wordingScanState === "error") {
                    void runWordingHeaderScan();
                  }
                }}
              />
            </Stack>
          </Box>
          <Stack spacing={2} sx={{ p: 1.5 }}>
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Accordion
            expanded={approvalToolsExpanded}
            onChange={(_, exp) => setApprovalToolsExpanded(exp)}
            disableGutters
            sx={FLAT_ACCORDION_SX}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="admin-approval-tools-content"
              id="admin-approval-tools-header"
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                <PendingActionsIcon color="primary" fontSize="small" aria-hidden />
                <Box>
                  <Typography fontWeight={600}>Review queue &amp; data integrity</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Matches the first five Needs Attention chips: Open approvals, Open TODOs, Duplicate event codes
                    (separate system and legacy checks), Duplicate contract names, Empty contract names. Latest-row
                    rules apply as documented in each panel. Then Rising / Resolving pair integrity (no chip).
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
              <Divider />
              <Box sx={{ px: 1, py: 1 }}>
                {approvalToolsExpanded ? (
                  <AdminApprovalTools
                    embedded
                    initialBacklogRows={prefetchedApprovalRows}
                    initialBacklogTotal={unapprovedTodoCount}
                    initialDevCommentRows={prefetchedTodoRows}
                    initialDevCommentTotal={devCommentTodoCount}
                    initialDuplicateReport={prefetchedDuplicateReport}
                    initialDuplicateLegacyReport={prefetchedDuplicateLegacyReport}
                    initialDuplicateContractNameReport={prefetchedDuplicateContractNameReport}
                    initialEmptyContractTotal={emptyContractUserEventCount}
                    initialEmptyContractRows={prefetchedEmptyContractRows}
                  />
                ) : null}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Accordion
            expanded={wordingGuardExpanded}
            onChange={(_, exp) => setWordingGuardExpanded(exp)}
            disableGutters
            sx={FLAT_ACCORDION_SX}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="admin-wording-guard-content"
              id="admin-wording-guard-header"
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                <TuneIcon color="primary" fontSize="small" aria-hidden />
                <Box>
                  <Typography fontWeight={600}>Wording guard</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Rules and violation analysis for wording consistency.
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
              <Divider />
              <AdminWordingGuardPanel
                active={wordingGuardExpanded}
                initialAnalysis={wordingLatestAnalysis}
                onAnalysisUpdated={(analysis) => {
                  setWordingLatestAnalysis(analysis);
                  setWordingViolationEntryCount(analysis.total_entries ?? 0);
                  setWordingScanState("done");
                }}
                needsAttentionWordingChip={{
                  scanState: wordingScanState,
                  violationEntryCount: wordingViolationEntryCount,
                  busy: wordingChipBusy,
                  onChipActivate: () => {
                    setWordingGuardExpanded(true);
                    if (wordingScanState === "not_run" || wordingScanState === "error") {
                      void runWordingHeaderScan();
                    }
                  },
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderColor: "info.light",
            backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(41, 182, 246, 0.07)" : "rgba(3, 169, 244, 0.04)",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
              Configuration &amp; Governance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Global rules and mappings that define system behavior.
            </Typography>
          </Box>
          <Stack spacing={2} sx={{ p: 1.5 }}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={eventReassignmentExpanded}
                onChange={(_, exp) => setEventReassignmentExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-event-reassignment-content"
                  id="admin-event-reassignment-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <SwapHorizIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Event reassignment (advanced)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Move single events or complete sub-ranges to a new base/sub range (preview first).
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <AdminEventReassignmentPanel active={eventReassignmentExpanded} />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={contractRenameExpanded}
                onChange={(_, exp) => setContractRenameExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-contract-rename-content"
                  id="admin-contract-rename-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <DriveFileRenameOutlineIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Contract rename (advanced)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Fix contract names in place per side; rising and/or resolving, optional separate new names.
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <AdminContractRenamePanel active={contractRenameExpanded} />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={exportCodesExpanded}
                onChange={(_, exp) => setExportCodesExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-export-codes-content"
                  id="admin-export-codes-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <AdminPanelSettingsIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Export event codes</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Global export mode (legacy old PLC codes vs new system codes).
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={
                        !settingsLoaded
                          ? "default"
                          : useLegacyEventCodesInExport
                            ? "warning"
                            : "success"
                      }
                      icon={
                        !settingsLoaded ? (
                          <CircularProgress size={12} thickness={5} sx={{ color: "inherit", ml: 0.25 }} />
                        ) : undefined
                      }
                      label={
                        !settingsLoaded
                          ? "Export mode"
                          : useLegacyEventCodesInExport
                            ? "Legacy"
                            : "New system"
                      }
                      sx={{ ml: "auto" }}
                      aria-busy={!settingsLoaded}
                    />
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
                    Applies to <strong>all users</strong> and all export jobs (EDF, Visu+, Exor alarms XML,
                    Kollmorgen ZIP, PDF, WebHMI, IPC): the API uses the global setting; legacy uses stored old PLC
                    codes for non-manufacturer events when available, otherwise current event codes.
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                      Who can view and save (Entra)
                    </Typography>
                    <AdminRoleInfoTooltip ariaLabel="Export settings permissions in Entra">
                      <>
                        This screen is shown when your account has the portal role <code>{ROLE_PORTAL_ADMIN}</code>{" "}
                        in the SPA. Saving this toggle calls the Event Manager API, which checks a separate{" "}
                        <strong>API application permission</strong> in Microsoft Entra (default name{" "}
                        <code>{ROLE_ADMIN_EXPORT_SETTINGS}</code> on the API app; override on the server with{" "}
                        <code>REQUIRED_ROLE_EXPORT_SETTINGS_ADMIN</code>). Assign that API role to users or groups who
                        may change export behaviour; if save returns forbidden, fix Entra assignment and obtain a new
                        access token (e.g. sign out and in).
                      </>
                    </AdminRoleInfoTooltip>
                  </Stack>
                  {exportSettingsError && (
                    <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                      {exportSettingsError}
                    </Typography>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useLegacyEventCodesInExport}
                        disabled={!settingsLoaded}
                        onChange={async (_, checked) => {
                          try {
                            setExportSettingsError(null);
                            setExportSettingsSavedOpen(false);
                            await setUseLegacyEventCodesInExport(checked);
                            setExportSettingsSavedBody(buildExportSettingsSavedMessage(checked));
                            setExportSettingsSavedOpen(true);
                          } catch {
                            setExportSettingsSavedOpen(false);
                            setExportSettingsError(
                              "Saving failed — check network, or sign in again after an administrator assigns export-settings permissions in Entra (see the info icon above for role names)."
                            );
                          }
                        }}
                        color="primary"
                      />
                    }
                    label={
                      !settingsLoaded
                        ? "Loading export settings…"
                        : useLegacyEventCodesInExport
                          ? "Legacy (old) codes in exports"
                          : "New system codes in exports"
                    }
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Accordion
            expanded={numberRangeLabelsExpanded}
            onChange={(_, exp) => setNumberRangeLabelsExpanded(exp)}
            disableGutters
            sx={FLAT_ACCORDION_SX}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="admin-number-range-labels-content"
              id="admin-number-range-labels-header"
            >
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                <AccountTreeIcon color="primary" fontSize="small" aria-hidden />
                <Box>
                  <Typography fontWeight={600}>Number range labels</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Base and sub display names and descriptions per language
                  </Typography>
                </Box>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
              <Divider />
              <AdminNumberRangeLabelsPanel
                active={numberRangeLabelsExpanded}
                onLabelsSaved={bumpNumberRangeData}
              />
            </AccordionDetails>
          </Accordion>
        </Paper>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            borderColor: "success.light",
            backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(102, 187, 106, 0.07)" : "rgba(76, 175, 80, 0.04)",
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.8 }}>
              Operations &amp; Traceability
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Investigation, monitoring, import comparison, and usage analytics.
            </Typography>
          </Box>
          <Stack spacing={2} sx={{ p: 1.5 }}>
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-csv-compare-content"
                  id="admin-csv-compare-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <CompareArrowsIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>CSV vs database compare</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        ekOpenVaf export vs imported UserEvents (event code)
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <EventCsvUpload embedded />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={auditExpanded}
                onChange={(_, exp) => setAuditExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-audit-log-content"
                  id="admin-audit-log-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <HistoryIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Event audit log</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Creates, edits, approvals, and deletes (who and when)
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <AdminAuditLogPanel active={auditExpanded} />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-event-history-explorer-content"
                  id="admin-event-history-explorer-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <TimelineIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                        Event history explorer
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Open transition-chain search for event code reassignment history.
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1.25, color: theme.palette.text.secondary }}>
                    Search reassignment transitions by event code in a dedicated view.
                  </Typography>
                  <Chip
                    size="small"
                    icon={<TimelineIcon />}
                    label="Open history explorer"
                    clickable
                    onClick={() => navigate("/event-history")}
                  />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-import-tool-content"
                  id="admin-import-tool-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <UploadFileIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Import tool (advanced)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Open the dedicated import page for bulk event ingestion.
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    Advanced operation: use this only for controlled imports.
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                      Who can open and run imports
                    </Typography>
                    <AdminRoleInfoTooltip ariaLabel="Import permissions in Entra">
                      <>
                        This entry is visible for users with the portal role <code>{ROLE_PORTAL_ADMIN}</code>.
                        Import API endpoints are checked server-side against a dedicated API app role configured
                        via backend environment variable <code>REQUIRED_ROLE_IMPORT</code>. If import calls return
                        forbidden, verify the Entra API role assignment and then sign out/sign in to refresh the token.
                      </>
                    </AdminRoleInfoTooltip>
                  </Stack>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => navigate("/import")}
                  >
                    Open import page
                  </Button>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={numberRangeUsageExpanded}
                onChange={(_, exp) => setNumberRangeUsageExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-number-ranges-content"
                  id="admin-number-ranges-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <AnalyticsIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Number range usage</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Fill levels and per-sub-function details — data loads when you expand
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <Box sx={{ px: 1.5, py: 2 }}>
                    {numberRangeUsageExpanded ? (
                      <AdminNumberRangeStatusPage labelsDataVersion={numberRangeDataVersion} />
                    ) : null}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={translationAdminExpanded}
                onChange={(_, exp) => setTranslationAdminExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-translation-content"
                  id="admin-translation-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <TranslateIcon color="primary" fontSize="small" aria-hidden />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={600}>Translations (incomplete languages)</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Find events missing ger/eng/ita/cze and queue Azure re-translation
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <Box sx={{ px: 2, pt: 1.25 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" component="span">
                        Who can view and queue translations
                      </Typography>
                      <AdminRoleInfoTooltip ariaLabel="Translation queue API permissions in Entra">
                        <>
                          Loading this list and queuing bulk re-translation call the Event Manager API, which requires
                          the <strong>API application role</strong> <code>{ROLE_API_ADMIN_APPROVE}</code> in Microsoft
                          Entra on the API app. Assign that role, then sign out and in if you see forbidden errors.
                        </>
                      </AdminRoleInfoTooltip>
                    </Stack>
                  </Box>
                  <AdminTranslationPanel active={translationAdminExpanded} />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion
                expanded={vehicleScanExpanded}
                onChange={(_, exp) => setVehicleScanExpanded(exp)}
                disableGutters
                sx={FLAT_ACCORDION_SX}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-vehicle-scan-content"
                  id="admin-vehicle-scan-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <DirectionsCarFilledIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>Vehicle software event scans</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        CSV uploads from pipelines (which software reported which event codes).
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                  <Divider />
                  <AdminVehicleSoftwareScanPanel active={vehicleScanExpanded} onOpenEvent={(id) => setEditEventId(id)} />
                </AccordionDetails>
              </Accordion>
            </Paper>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Accordion defaultExpanded={false} disableGutters sx={FLAT_ACCORDION_SX}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="admin-user-activity-content"
                  id="admin-user-activity-header"
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: "100%", pr: 1 }}>
                    <HistoryIcon color="primary" fontSize="small" aria-hidden />
                    <Box>
                      <Typography fontWeight={600}>User activity log</Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        View user workspace and event actions for substitution/audit.
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pt: 1, pb: 2 }}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                    <TextField
                      size="small"
                      label="Filter by user UUID"
                      value={adminUserFilter}
                      onChange={(e) => setAdminUserFilter(e.target.value)}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setAdminActivityOffset(0);
                        void loadAdminActivity();
                      }}
                      disabled={adminActivityLoading}
                    >
                      {adminActivityLoading ? "Loading..." : "Refresh"}
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {adminActivityOffset + 1}-{Math.min(adminActivityOffset + adminActivityRows.length, adminActivityTotal)} of {adminActivityTotal}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={adminActivityOffset === 0 || adminActivityLoading}
                        onClick={() => setAdminActivityOffset((prev) => Math.max(0, prev - adminActivityLimit))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={adminActivityOffset + adminActivityLimit >= adminActivityTotal || adminActivityLoading}
                        onClick={() => setAdminActivityOffset((prev) => prev + adminActivityLimit)}
                      >
                        Next
                      </Button>
                    </Stack>
                  </Stack>
                  <Stack spacing={1}>
                    {adminActivityRows.map((row) => (
                      <Paper key={row.id} variant="outlined" sx={{ p: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {row.user_display_name || row.user_uuid} - {activityActionLabel(row.action_type)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatLocalDateTime(row.created_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {row.entity_type} {row.entity_id}
                              {row.entity_type === "creator_session" &&
                              typeof (row.metadata as any)?.title === "string" &&
                              (row.metadata as any).title.trim() !== ""
                                ? ` - "${(row.metadata as any).title}"`
                                : ""}
                            </Typography>
                          </Box>
                          {row.entity_type === "creator_session" && /^\d+$/.test(String(row.entity_id)) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                navigate("/event-configurator", {
                                  state: { workspaceSessionId: Number(row.entity_id) },
                                })
                              }
                            >
                              Continue
                            </Button>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                    {!adminActivityLoading && adminActivityRows.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No user activity entries found.
                      </Typography>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Paper>

          </Stack>
        </Paper>
      </Stack>

      <CustomAlert
        open={exportSettingsSavedOpen}
        onClose={() => setExportSettingsSavedOpen(false)}
        message={exportSettingsSavedBody ?? ""}
        severity="success"
        duration={12000}
      />
      <EventsFormDialog
        open={Boolean(editEventId)}
        onClose={() => setEditEventId(null)}
        onSaved={() => {
          setEditEventId(null);
          void refreshDataQualityMetrics();
        }}
        eventId={editEventId}
        createNewEvent={false}
        onNavigateToEvent={(id) => setEditEventId(id)}
      />
    </Container>
  );
};

export default AdminPage;
